import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  IconButton,
  Typography,
  styled,
  TextField,
  Grid,
} from '@mui/material';

export const SectionTitle = styled(Typography)(() => ({
  fontSize: '18px',
  fontWeight: 600,
  marginBottom: '14px',
}));

export const ActionBar = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  paddingTop: '16px',
  borderTop: '1px solid #EBEBEB',
  marginTop: '8px',
}));

export const ApproveRejectBar = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
  marginTop: '24px',
}));

export const EditButton = styled(Button)(({ theme }) => ({
  width: '200px',
  display: 'flex',
  alignContent: 'center',
  gap: '8px',
  border: `1px solid ${theme.palette.secondary.main}`,
  borderRadius: '12px',
  color: theme.palette.secondary.main,
  backgroundColor: 'transparent',
  textTransform: 'lowercase',
  '&:disabled': {
    color: theme.palette.secondary.main,
  },
  span: { marginTop: '2px' },
}));

export const SaveButton = styled(Button)(() => ({
  borderRadius: '12px',
  width: '200px',
}));

export const RejectButton = styled(Button)(() => ({
  width: '200px',
  backgroundColor: '#FAFCFF',
  fontWeight: 600,
  fontSize: 15,
  lineHeight: '20px',
  border: '2px solid #FF334B',
  color: '#FF334B',
  display: 'flex',
  gap: 8,
  '&:hover': {
    backgroundColor: '#FF334B',
  },
  '&:active': {
    backgroundColor: '#FF334B',
  },
  '&:disabled': {
    color: '#FF334B',
  },
}));

export const ApproveButton = styled(Button)(({ theme }) => ({
  width: '200px',
  backgroundColor: theme.palette.primary.dark,
  color: '#FFF',
  textTransform: 'lowercase',
  '&:hover': { filter: 'brightness(85%)' },
  '&:disabled': { opacity: '70%' },
}));

export const RegisterBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px',
  border: `1px dashed #ADADAD`,
  borderRadius: '12px',
  marginTop: '12px',
}));

export const DocumentBox = styled(Box)(() => ({
  padding: '20px',
  border: `1px dashed #ADADAD`,
  borderRadius: '12px',
  marginTop: '12px',
}));

export const InputTitle = styled(Typography)(() => ({
  paddingBottom: '4px',
  fontSize: '14px',
  textTransform: 'capitalize',
}));

export const Content = styled(Typography)(() => ({
  fontWeight: '500',
  textTransform: 'capitalize',
}));

export const CompanionBox = styled(Box)(() => ({
  border: '1px solid #D7D8DB',
  borderRadius: '8px',
  padding: '16px',
}));

export const FileList = styled('ul')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  padding: 0,
  listStyleType: 'none',
  margin: 0,
  '& span': { color: theme.palette.secondary.main },
}));

export const SubmitFileBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: '24px',
  margin: '24px 0',
  width: '100%',
}));

export const CancelFileButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  fontWeight: '700',
  color: theme.palette.secondary.main,
  border: `2px solid ${theme.palette.secondary.main}`,
  borderRadius: '30px',
  width: '150px',
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
  },
  [theme.breakpoints.up('xl')]: {
    width: '220px',
  },
}));

export const SubmitFileButton = styled(Button)(({ theme }) => ({
  width: '150px',
  backgroundColor: theme.palette.secondary.main,
  '&:hover': {
    filter: 'brightness(85%)',
  },
  [theme.breakpoints.up('xl')]: {
    width: '220px',
  },
}));

export const TextTypography = styled(Typography)(() => ({
  textAlign: 'center',
  fontSize: '18px',
  color: '#595959',
}));

export const RejectTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    '&::-webkit-scrollbar': {
      width: 10,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.secondary.main,
      borderRadius: 10,
      '&:hover': {
        backgroundColor: theme.palette.secondary.dark,
      },
    },
  },
}));

export const FileContainer = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  borderBottom: '1px solid #ccc',
  padding: '8px 0 8px 4px',
  '&:last-child': { borderBottom: 'none' },
}));

export const AttachmentsBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

export const DialogTitle = styled(Typography)(({ theme }) => ({
  font: '700 21px Montserrat',
  textTransform: 'capitalize',
  color: '#595959',
  marginBottom: 24,
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  '& span': { color: theme.palette.secondary.main },
}));

export const FileNameTypography = styled(Typography)(({ theme }) => ({
  minWidth: '300px',
  display: 'flex',
  gap: '6px',
  alignContent: 'center',
  color: '#595959',
  font: '400 16px Montserrat',
  '& span': {
    color: theme.palette.secondary.main,
    fontSize: 20,
  },
}));

export const StyledIcon = styled(ButtonBase)(({ theme }) => ({
  span: {
    color: theme.palette.secondary.main,
    fontSize: 20,
    padding: '4px',
    borderRadius: '50%',
  },
}));

export const InputBox = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  marginTop: '24px',
}));

const statusColors: Record<string, string> = {
  approved: '#2e9133',
  rejected: '#992626',
};

export const StatusTag = styled(Typography)<{ status: string }>(
  ({ status }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    border: `2px solid ${statusColors[status] ?? '#595959'}`,
    borderRadius: '10px',
    padding: '4px 16px',
    color: statusColors[status] ?? '#595959',
    fontWeight: '600',
    textTransform: 'capitalize',
  })
);

export const StyledDialog = styled(Dialog)(() => ({
  '& .MuiPaper-root': {
    borderRadius: '16px',
    backgroundColor: '#FAFCFF',
  },
}));

export const DialogContainer = styled(Box)(() => ({
  minWidth: '600px',
  minHeight: '200px',
  padding: '40px 40px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
}));

export const ButtonBox = styled(Box)(() => ({
  margin: '20px 0',
  display: 'flex',
  gap: '40px',
  justifyContent: 'center',
}));

export const OutlineButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  width: '100%',
  border: `1px solid ${theme.palette.secondary.main}`,
  color: theme.palette.secondary.main,
  backgroundColor: 'transparent',
  fontWeight: '600',
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
  },
}));

export const OutlinedButton = styled(Button)(({ theme }) => ({
  maxWidth: '150px',
  border: `1.5px solid ${theme.palette.secondary.main}`,
  color: theme.palette.secondary.main,
  backgroundColor: 'transparent',
  textTransform: 'lowercase',
}));

export const FilledButton = styled(Button)(({ theme }) => ({
  maxWidth: '150px',
  backgroundColor: theme.palette.secondary.main,
  textTransform: 'lowercase',
  '&:hover': {
    filter: 'brightness(85%)',
  },
}));

type AttachFileButtonProps = {
  component: string;
};

export const AttachFileButon = styled(Button)<AttachFileButtonProps>(
  ({ component, theme }) => ({
    component,
    width: '170px',
    margin: '10px',
    color: '#FFFFFF',
    backgroundColor: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
      filter: 'brightness(85%)',
    },
    [theme.breakpoints.up('xl')]: {
      width: '220px',
    },
    '&:disabled': {
      filter: 'opacity(50%)',
    },
  })
);

/* -------------------------------------------------------------------------
 * ReportAttachmentsModal (PROT-2980)
 *
 * Estilos EXCLUSIVOS do modal que entrega o laudo. O prefixo `Report` é
 * deliberado: `OutlinedButton`, `FilledButton` e `ButtonBox` são compartilhados
 * com ApproveConfirmationDialog, RejectConfirmationDialog e DeedUpload, onde os
 * rótulos são curtos e cabem nos 150px. Foi justamente usar aqueles como CAMPO
 * DE ARQUIVO que quebrou esta tela — nada abaixo deve voltar para lá.
 * ------------------------------------------------------------------------- */

export const ReportFieldBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  // Deixa a coluna encolher: sem isto os filhos com reticência não truncam.
  minWidth: 0,
}));

export const ReportFieldLabel = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: 600,
  color: '#595959',
}));

export const ReportDropArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'dragActive',
})<{ dragActive: boolean }>(({ theme, dragActive }) => ({
  // Largura total em vez de uma pílula de 150px: é o que elimina a quebra de
  // texto por construção, em vez de esticar o limite até o rótulo atual caber.
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px 16px',
  borderRadius: '16px',
  border: `2px dashed ${dragActive ? theme.palette.primary.main : '#C7C7C7'}`,
  backgroundColor: dragActive ? '#F0F4FA' : 'transparent',
  transition: 'border-color 0.15s, background-color 0.15s',
  cursor: 'pointer',
}));

export const ReportDropLabel = styled('label')(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  textAlign: 'center',
  cursor: 'pointer',
}));

export const ReportDropIcon = styled(Typography)(({ theme }) => ({
  display: 'flex',
  '& span': { fontSize: '28px', color: theme.palette.primary.main },
}));

export const ReportDropTitle = styled(Typography)(() => ({
  fontSize: '14px',
  color: '#595959',
}));

export const ReportDropSubtitle = styled(Typography)(() => ({
  fontSize: '12px',
  color: '#ADADAD',
}));

export const ReportFileRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
  padding: '8px 12px',
  borderRadius: '10px',
  border: '1px solid #EBEBEB',
  backgroundColor: '#FFFFFF',
}));

export const ReportFileIcon = styled(Typography)(({ theme }) => ({
  display: 'flex',
  flexShrink: 0,
  '& span': { fontSize: '20px', color: theme.palette.primary.main },
}));

export const ReportFileName = styled(Typography)(() => ({
  flex: 1,
  // `minWidth: 0` é o detalhe que faz a reticência funcionar: por padrão um
  // item de flex não encolhe abaixo do próprio conteúdo, então a linha
  // ESTICARIA em vez de truncar e o `textOverflow` nunca apareceria.
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '14px',
  color: '#595959',
}));

export const ReportFileSize = styled(Typography)(() => ({
  flexShrink: 0,
  fontSize: '12px',
  color: '#ADADAD',
}));

export const ReportTypeSelect = styled(TextField)(() => ({
  flexShrink: 0,
  width: '180px',
}));

export const ReportRemoveButton = styled(IconButton)(() => ({
  flexShrink: 0,
  color: '#ADADAD',
  '& span': { fontSize: '20px' },
  '&:hover': { color: '#FF334B' },
}));

export const ReportFooter = styled(Box)(() => ({
  marginTop: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '12px',
}));

export const ReportFooterHint = styled(Typography)(() => ({
  flex: 1,
  minWidth: 0,
  fontSize: '12px',
  color: '#ADADAD',
}));
